/**
 * The lite slot host «Без миниатюр» (architecture §11, GDD C185, ui-ux §6.26): while `gfx.lite` is on, every stage slot
 * `.mc-stage-fallback` (ui-ux §4.6) gets a painted plate, so no screen shows an empty stage. Screens choose the plate
 * with `data-plate="stage|arena|map|crypt|table"` and an optional `data-species` bust.
 *
 * Until F5's painted plates (`scenes/*`, `species/*`) and F3's `artImage()` exist, the plates are drawn here as small
 * illuminated miniatures (SVG): a lapis starry vault under a vermilion-and-gold arch. P10 owns the final look.
 */

export type PlateKind = 'stage' | 'arena' | 'map' | 'crypt' | 'table';

const STARS = [[42, 38, 3], [88, 26, 2.2], [130, 52, 2.6], [178, 30, 3.2], [222, 46, 2.2], [256, 70, 2.8], [60, 78, 2], [150, 88, 1.8], [208, 96, 2.2], [100, 64, 1.6]] as const;
const star = ([x, y, r]: readonly [number, number, number]): string =>
  `<path d="M${x} ${y - r * 2.2} L${x + r * 0.55} ${y - r * 0.55} L${x + r * 2.2} ${y} L${x + r * 0.55} ${y + r * 0.55} L${x} ${y + r * 2.2} L${x - r * 0.55} ${y + r * 0.55} L${x - r * 2.2} ${y} L${x - r * 0.55} ${y - r * 0.55} Z" fill="#F3D77A" stroke="#9C7424" stroke-width=".5"/>`;

const GROUND: Record<PlateKind, string> = {
  stage: '<path d="M14 170 C70 150 120 158 150 152 C190 146 240 156 286 166 V200 H14 Z" fill="#3C8C74"/><path d="M14 182 C80 170 200 172 286 180 V200 H14 Z" fill="#24594A"/>',
  arena: '<path d="M14 168 H286 V200 H14 Z" fill="#C9A04A"/><path d="M14 168 H286" stroke="#5C3A22" stroke-width="2"/><path d="M40 186 h40 M120 180 h50 M210 188 h46" stroke="#9C7424" stroke-width="1.4" stroke-linecap="round"/>',
  map: '<path d="M14 160 C60 140 90 170 140 150 C190 132 230 160 286 146 V200 H14 Z" fill="#3C8C74"/><path d="M40 190 C80 170 120 182 150 168 S220 160 260 170" stroke="#C8341E" stroke-width="2.4" stroke-dasharray="5 5" fill="none"/>',
  crypt: '<path d="M14 150 H286 V200 H14 Z" fill="#5C3A22"/><path d="M14 150 H286 M60 150 V200 M120 150 V200 M180 150 V200 M240 150 V200 M14 175 H286" stroke="#3A2414" stroke-width="2"/>',
  table: '<path d="M14 150 H286 V200 H14 Z" fill="#B5612B"/><path d="M14 150 H286" stroke="#5C3A22" stroke-width="3"/><rect x="118" y="132" width="30" height="42" rx="3" fill="#FBF5E4" stroke="#2B1D14" stroke-width="2" transform="rotate(-8 133 153)"/><rect x="150" y="132" width="30" height="42" rx="3" fill="#FBF5E4" stroke="#2B1D14" stroke-width="2" transform="rotate(7 165 153)"/>',
};

/** A cat-ish bust in ink (placeholder for the painted `species/*` busts). */
const BUST = `<g transform="translate(150 132)">
  <path d="M-26 34 C-26 10 -18 0 0 0 C18 0 26 10 26 34 Z" fill="#C8341E" stroke="#2B1D14" stroke-width="2"/>
  <path d="M-8 14 H8 M0 6 V24" stroke="#FBF5E4" stroke-width="4"/>
  <path d="M-21 -8 L-24 -34 L-8 -22 Z M21 -8 L24 -34 L8 -22 Z" fill="#C9A04A" stroke="#2B1D14" stroke-width="2" stroke-linejoin="round"/>
  <ellipse cx="0" cy="-10" rx="22" ry="19" fill="#C9A04A" stroke="#2B1D14" stroke-width="2"/>
  <path d="M-14 -20 l5 3 M14 -20 l-5 3 M-16 -6 l8 0 M-6 -10 C-6 -7 -6 -7 -4 -7 M16 -6 l-8 0" stroke="#5C3A22" stroke-width="1.6" stroke-linecap="round"/>
  <path d="M-12 -12 C-10 -14 -7 -14 -5 -12 M12 -12 C10 -14 7 -14 5 -12" stroke="#2B1D14" stroke-width="2" fill="none" stroke-linecap="round"/>
  <path d="M-2 -4 L2 -4 L0 -1 Z M0 -1 C-1 3 -5 3 -6 1 M0 -1 C1 3 5 3 6 1" stroke="#2B1D14" stroke-width="1.4" fill="#C76B7E"/>
  <circle cx="0" cy="-10" r="27" fill="none" stroke="#D4A537" stroke-width="2.4" stroke-dasharray="4 3" opacity=".9"/>
</g>`;

/** An illuminated placeholder plate as an SVG string (300 × `h`, default 200; the title uses a tall 300 × 360). */
export function plateSvg(kind: PlateKind, withBust: boolean, h = 200, bustScale = 1): string {
  const arch = `M14 ${h} V70 C14 40 40 18 80 12 C110 8 130 4 150 2 C170 4 190 8 220 12 C260 18 286 40 286 70 V${h}`;
  const ground = `<g transform="translate(0 ${h - 200})">${GROUND[kind]}</g>`;
  const bust = withBust ? `<g transform="translate(0 ${h - 200}) translate(150 166) scale(${bustScale}) translate(-150 -166)"><g class="mc-bust">${BUST}</g></g>` : '';
  const extraStars = h > 200 ? STARS.map(([x, y, r]) => star([300 - x, y + 90, r * 0.8])).join('') : '';
  return `<svg class="mc-lite-plate__art" viewBox="0 0 300 ${h}" preserveAspectRatio="xMidYMid slice" role="img" aria-hidden="true">
  <rect width="300" height="${h}" fill="#F1E4C3"/>
  <path d="${arch} Z" fill="#1B2F7A"/>
  <path d="${arch} Z" fill="none" stroke="#0F1C4D" stroke-width="10" opacity=".35"/>
  ${STARS.map(star).join('')}${extraStars}
  ${ground}
  ${bust}
  <path d="${arch}" fill="none" stroke="#C8341E" stroke-width="9"/>
  <path d="${arch}" fill="none" stroke="#D4A537" stroke-width="2"/>
  <path d="M4 ${h} V64 C4 30 34 8 78 2 M296 ${h} V64 C296 30 266 8 222 2" fill="none" stroke="#D4A537" stroke-width="2"/>
</svg>`;
}

const STYLE = `
.mc-stage-fallback[data-lite-filled] { overflow: hidden; background: #F1E4C3; }
.mc-lite-plate { position: absolute; inset: 0; display: block; }
.mc-lite-plate__art { width: 100%; height: 100%; display: block; }
`;

/** Plate kind for a slot: its own `data-plate`, else the F3 stage slot's `data-scene` (ScenePreset kinds). */
function kindOf(slot: HTMLElement): PlateKind {
  const own = slot.dataset.plate as PlateKind | undefined;
  if (own && GROUND[own]) return own;
  const scene = (slot.closest('[data-scene]') as HTMLElement | null)?.dataset.scene ?? '';
  if (scene === 'arena') return 'arena';
  if (scene === 'folioMap' || scene === 'map') return 'map';
  if (scene === 'crypt') return 'crypt';
  if (scene === 'cardTable' || scene === 'table') return 'table';
  return 'stage';
}

/**
 * Fills one slot with a plate (idempotent) and un-hides it. A slot that already has content (a screen called F3's
 * `slot.showFallback(node)` with its own bust or plate) is left alone.
 */
export function fillSlot(slot: HTMLElement): void {
  if (slot.dataset.liteFilled) return;
  if ([...slot.children].some((c) => !c.classList.contains('mc-lite-plate'))) return;
  const kind = kindOf(slot);
  const plate = document.createElement('div');
  plate.className = 'mc-lite-plate';
  plate.innerHTML = plateSvg(kind, slot.dataset.species !== undefined || kind === 'stage' || kind === 'arena');
  slot.dataset.liteFilled = '1';
  slot.prepend(plate);
  slot.hidden = false;
}

/** Watches `root` and fills every `.mc-stage-fallback` while lite mode is on. Returns a stop function. */
export function startLiteHost(root: HTMLElement, isLite: () => boolean): () => void {
  if (!document.getElementById('mc-lite-style')) {
    const s = document.createElement('style');
    s.id = 'mc-lite-style';
    s.textContent = STYLE;
    document.head.appendChild(s);
  }
  const scan = (): void => {
    if (!isLite()) return;
    for (const slot of root.querySelectorAll<HTMLElement>('.mc-stage-fallback:not([data-lite-filled])')) fillSlot(slot);
  };
  const mo = new MutationObserver(scan);
  mo.observe(root, { childList: true, subtree: true });
  scan();
  return () => mo.disconnect();
}
